// src/components/admin/attendance/ComprehensiveAttendanceManagement.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Calendar, Users, Clock, Download, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
    CheckCircle, XCircle, AlertCircle, Settings, FileText, Filter, Home, X, PartyPopper, BarChart3, MapPin
} from 'lucide-react';
import { api } from '../../../services/api';
import employeeService from '../../../services/employee.service';
import holidayService from '../../../services/holiday.service';
import AttendanceCalendarEnhanced from './AttendanceCalendarEnhanced';
import HolidayManagement from './HolidayManagement';
import LeavePlanManagement from './LeavePlanManagement';
import RegulateAttendance from './RegulateAttendance';
import AttendanceAnalytics from './AttendanceAnalytics';
import GeoLocation from '../GeoLocation';
import BiometricManagement from '../BiometricManagement';
import { toISTDateString, getCurrentISTDate, parseISTDate, formatTime, formatDate, getISTComponents } from '../../../utils/dateHelpers';
import { useAIAvatar } from '../../common/aia/AIAvatarContext';
import { getTourById } from '../../common/aia/tours';

const ComprehensiveAttendanceManagement = () => {
    const navigate = useNavigate();
    const { currentTour, tourStep, userRole } = useAIAvatar();

    // Initialize with IST date to avoid timezone issues
    const getLocalDateString = () => {
        return getCurrentISTDate();
    };

    // Helper function to parse weekly off string into day numbers
    const parseWeeklyOff = (weeklyOffStr) => {
        if (!weeklyOffStr) return [];

        const weeklyOffStr_lower = String(weeklyOffStr).toLowerCase();
        const dayMap = {
            'sunday': 0, 'sundays': 0, 'sun': 0,
            'monday': 1, 'mondays': 1, 'mon': 1,
            'tuesday': 2, 'tuesdays': 2, 'tue': 2, 'tues': 2,
            'wednesday': 3, 'wednesdays': 3, 'wed': 3,
            'thursday': 4, 'thursdays': 4, 'thu': 4, 'thur': 4, 'thurs': 4,
            'friday': 5, 'fridays': 5, 'fri': 5,
            'saturday': 6, 'saturdays': 6, 'sat': 6
        };

        // Remove common non-day words (only, off, day, days)
        const cleanedStr = weeklyOffStr_lower
            .replace(/\b(only|off|day|days)\b/g, '') // Remove filler words
            .replace(/\s+/g, ' ')  // Normalize spaces
            .trim();

        // Split by comma, ampersand, "and", or spaces
        const parts = cleanedStr.split(/[,&]|\band\b|\s+/);
        const days = [];

        parts.forEach(part => {
            const trimmed = part.trim();
            if (trimmed && dayMap.hasOwnProperty(trimmed)) {
                const dayNum = dayMap[trimmed];
                if (!days.includes(dayNum)) {
                    days.push(dayNum);
                }
            }
        });

        return days;
    };

    // Check if date is week-off for employee (using IST day of week)
    const isWeekOff = (date, employee) => {
        if (!employee || !employee.weekly_off) return false;
        const weeklyOffDays = parseWeeklyOff(employee.weekly_off);
        const { dayOfWeek } = getISTComponents(date);
        return weeklyOffDays.includes(dayOfWeek);
    };

    // Get normalized employee ID
    const getEmployeeId = (record) => {
        return record.employee_id || record.id || record.emp_id;
    };

    // Status priority for conflict resolution (higher number = higher priority)
    const STATUS_PRIORITY = {
        'inactive': 7,  // Highest priority - can't be overridden
        'leave': 6,      // Approved leave is high priority
        'holiday': 5,    // Company holiday
        'week_off': 4,   // Regular day off
        'present': 3,    // Actually worked
        'half-day': 2,   // Half day
        'absent': 1      // Lowest priority
    };

    // Helper function to parse API timestamp (same as AttendanceCalendar)
    const parseApiTimestamp = (timestamp) => {
        if (!timestamp) return null;
        if (timestamp instanceof Date) {
            return Number.isNaN(timestamp.getTime()) ? null : timestamp;
        }

        const trimmed = String(timestamp).trim();
        if (!trimmed) return null;

        // If it has timezone info, parse directly
        if (/([Zz]|[+-]\d{2}:?\d{2})$/.test(trimmed)) {
            const parsed = new Date(trimmed);
            return Number.isNaN(parsed.getTime()) ? null : parsed;
        }

        // Try to parse as local datetime (treat as IST)
        const normalized = trimmed.replace('T', ' ');
        const localMatch = normalized.match(
            /^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,6}))?)?)?$/
        );

        if (localMatch) {
            const [, year, month, day, hour = '0', minute = '0', second = '0', fractional = '0'] = localMatch;
            const milliseconds = fractional
                ? Math.round(parseFloat(`0.${fractional}`) * 1000)
                : 0;

            const pad = (n) => String(n).padStart(2, '0');
            const isoWithIST = `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}:${pad(second)}.${String(milliseconds).padStart(3, '0')}+05:30`;
            const parsed = new Date(isoWithIST);

            return Number.isNaN(parsed.getTime()) ? null : parsed;
        }

        const parsed = new Date(trimmed);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    // Helper function to determine time check status (early, on-time, late)
    const getTimeCheckStatus = (checkInTime, shiftStartTime, graceMinutes = 0) => {
        if (!checkInTime || !shiftStartTime) return null;

        // Parse the check_in_time timestamp
        const checkInDate = parseApiTimestamp(checkInTime);
        if (!checkInDate) return null;

        // Extract hour and minute from check-in time using IST timezone
        const { hour: checkInHour, minute: checkInMin } = getISTComponents(checkInDate);

        // Parse shift start time (format: "HH:MM" or "HH:MM:SS")
        const shiftParts = String(shiftStartTime).split(':');
        const shiftHour = parseInt(shiftParts[0]) || 0;
        const shiftMin = parseInt(shiftParts[1]) || 0;

        const checkInMinutes = checkInHour * 60 + checkInMin;
        const shiftMinutes = shiftHour * 60 + shiftMin;
        const grace = parseInt(graceMinutes) || 15; // Default 15 min grace

        if (checkInMinutes < shiftMinutes) return 'early';
        if (checkInMinutes <= shiftMinutes + grace) return 'on-time';
        return 'late';
    };

    const [selectedDate, setSelectedDate] = useState(getLocalDateString());
    const [activeTab, setActiveTab] = useState('overview'); // overview, holidays
    const tabLocation = useLocation();

    // Sync activeTab from URL ?tab= param (for sidebar sub-navigation)
    useEffect(() => {
        const params = new URLSearchParams(tabLocation.search);
        const tab = params.get('tab');
        if (tab) setActiveTab(tab);
    }, [tabLocation.search]);

    const [expandedEmployees, setExpandedEmployees] = useState(new Set());
    const [attendanceData, setAttendanceData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all'); // all, present, absent, leave
    const [filterEmployee, setFilterEmployee] = useState(''); // employee name search
    const [filterDepartment, setFilterDepartment] = useState('all'); // all or specific department
    const [filterLocation, setFilterLocation] = useState('all'); // all, wfh, office
    const [totalEmployees, setTotalEmployees] = useState(0);
    const [allEmployees, setAllEmployees] = useState([]);
    const [monthlyCache, setMonthlyCache] = useState({}); // Cache monthly attendance data for details
    const [calendarCache, setCalendarCache] = useState(null); // Separate cache for calendar widget
    const [isCalendarExpanded, setIsCalendarExpanded] = useState(false); // Calendar widget state
    const [stats, setStats] = useState({
        total: 0,
        present: 0,
        absent: 0,
        leave: 0,
        wfh: 0,
        office: 0
    });

    // Ref for scrolling to details section
    const detailsRef = React.useRef(null);

    const selectedDateObj = parseISTDate(selectedDate) || new Date();
    const currentMonth = selectedDateObj.getMonth();
    const currentYear = selectedDateObj.getFullYear();

    // Fetch total employee count and all employees
    useEffect(() => {
        const fetchTotalEmployees = async () => {
            try {
                const employees = await employeeService.getAllEmployees();
                console.log('👥 Fetched all employees:', employees.length);
                console.log('👥 First employee structure:', employees);
                setTotalEmployees(employees.length);
                setAllEmployees(employees);
            } catch (error) {
                console.error('Error fetching total employees:', error);
                setTotalEmployees(0);
                setAllEmployees([]);
            }
        };

        fetchTotalEmployees();
    }, []);

    // Clear attendance cache on component mount to ensure fresh data after regularization approval
    useEffect(() => {
        console.log('🔄 Clearing attendance cache on page load');
        setMonthlyCache({});
        setCalendarCache(null);
    }, []);

    useEffect(() => {
        if (totalEmployees > 0) {
            fetchAttendanceData();
        }
    }, [selectedDate, totalEmployees]);

    // Handler to receive monthly data from calendar
    const handleMonthDataLoaded = (monthData) => {
        console.log('📅 Received monthly data from calendar:', monthData);
        // Store in both caches
        setMonthlyCache(monthData);
        setCalendarCache(monthData); // Store for calendar to use on reopen
    };

    // Process cached data without API calls using same deduplication logic
    const processCachedData = (cachedAttendanceData, allLeaves, summary) => {
        console.log('📦 Processing cached data with deduplication');

        // Create employee status map for deduplication
        const employeeStatusMap = new Map();
        const selectedDay = parseISTDate(selectedDate);
        const selectedDateStr = toISTDateString(selectedDate);

        // Step 1: Initialize all employees with default status
        // Cached data already has correct statuses, but we still need to ensure no duplicates
        allEmployees.forEach(emp => {
            const empId = getEmployeeId(emp);
            const joiningDateStr = emp.joining_date ? toISTDateString(emp.joining_date) : null;

            // Check if inactive (before joining date)
            let initialStatus = 'absent';
            if (joiningDateStr && selectedDateStr < joiningDateStr) {
                initialStatus = 'inactive';
            }

            employeeStatusMap.set(empId, {
                employee_id: empId,
                id: empId,
                employee_code: emp.employee_code,
                first_name: emp.first_name,
                last_name: emp.last_name,
                department_name: emp.department_name,
                status: initialStatus,
                joining_date: emp.joining_date,
                weekly_off: emp.weekly_off,
                shift_start_time: emp.shift_start_time,
                shift_grace_period: emp.shift_grace_period,
                check_in_time: null,
                check_out_time: null,
                work_hours: 0,
                work_location: null
            });
        });

        // Step 2: Process cached attendance data (includes present, leave, holiday, week_off)
        if (cachedAttendanceData && Array.isArray(cachedAttendanceData)) {
            cachedAttendanceData.forEach(record => {
                const empId = getEmployeeId(record);
                const existing = employeeStatusMap.get(empId);

                if (existing) {
                    // Use the cached record's status (already correctly determined by calendar)
                    employeeStatusMap.set(empId, {
                        ...existing,
                        ...record,
                        // Preserve important fields
                        employee_code: record.employee_code || existing.employee_code,
                        first_name: record.first_name || existing.first_name,
                        last_name: record.last_name || existing.last_name,
                        department_name: record.department_name || existing.department_name
                    });
                }
            });
        }

        // Convert map to array - each employee appears exactly once
        const completeAttendanceData = Array.from(employeeStatusMap.values());

        // Calculate clean stats from deduplicated data
        const stats = {
            total: 0,
            present: 0,
            absent: 0,
            leave: 0,
            holiday: 0,
            weekOff: 0,
            inactive: 0,
            wfh: 0,
            office: 0
        };

        completeAttendanceData.forEach(emp => {
            if (emp.status === 'inactive') {
                stats.inactive++;
            } else {
                stats.total++; // Only count active employees

                switch(emp.status) {
                    case 'present':
                        stats.present++;
                        // Check WFH/Office location
                        const locationFromWfh = (emp.work_location_from_wfh || '').toLowerCase();
                        const locationFromPunch = (emp.work_location || '').toLowerCase();
                        const locationFromRegularization = (emp.location?.type || '').toLowerCase();

                        const isWFH = locationFromWfh.includes('wfh') ||
                                     locationFromPunch === 'wfh' ||
                                     locationFromPunch.includes('home') ||
                                     locationFromRegularization.includes('wfh');

                        if (isWFH) {
                            stats.wfh++;
                        } else {
                            stats.office++;
                        }
                        break;
                    case 'absent':
                        stats.absent++;
                        break;
                    case 'leave':
                        stats.leave++;
                        break;
                    case 'holiday':
                        stats.holiday++;
                        break;
                    case 'week_off':
                        stats.weekOff++;
                        break;
                }
            }
        });

        console.log('✅ Cached data processed:', stats);

        setAttendanceData(completeAttendanceData);
        setStats(stats);
    };

    const fetchAttendanceData = async () => {
        try {
            // Check if we have complete cached data for this date
            if (monthlyCache.detailed && monthlyCache.detailed[selectedDate]) {
                console.log('✨ Using complete cached data for', selectedDate, '- NO API CALL!');

                const cachedSummary = monthlyCache.summary[selectedDate];
                const cachedAttendance = monthlyCache.detailed[selectedDate];
                const allLeaves = monthlyCache.leaves || [];

                setLoading(true); // Brief flash to show we're updating

                // Process cached data immediately
                processCachedData(cachedAttendance, allLeaves, cachedSummary);

                setLoading(false);
                return; // Skip API call completely!
            }

            // If no cache, fetch from API
            setLoading(true);
            console.log('🌐 No cache found, fetching from API for', selectedDate);

            // Fetch all required data in parallel: attendance, leaves, and holiday status
            const [attendanceResponse, leaveResponse, holidayResponse] = await Promise.all([
                api.get(`/admin/attendance?date=${selectedDate}`),
                api.get(`/api/leaves/all-requests`), // Get all leave requests
                holidayService.isHoliday(selectedDate) // Check if selected date is a holiday
            ]);

            const attendanceData = attendanceResponse.data.attendance || [];
            const allLeaveData = leaveResponse.data.leaves || leaveResponse.data.requests || leaveResponse.data || [];
            const isHoliday = holidayResponse.isHoliday || false;
            const holidayDetails = holidayResponse.holiday || null;

            console.log('🔍 Fetched attendance data:', attendanceData.length, 'records');
            console.log('🔍 Fetched all leave requests:', allLeaveData.length, 'records');
            console.log('🎉 Is Holiday:', isHoliday, holidayDetails ? `(${holidayDetails.name})` : '');
            console.log('🔍 First attendance record structure:', attendanceData[0]);
            console.log('🔍 First leave record structure:', allLeaveData[0]);

            // Create employee status map for deduplication
            const employeeStatusMap = new Map();
            const selectedDay = parseISTDate(selectedDate);
            const selectedDateStr = toISTDateString(selectedDate);

            // Step 1: Initialize all employees with default status
            console.log('📊 Step 1: Initializing all employees with base status');
            allEmployees.forEach(emp => {
                const empId = getEmployeeId(emp);
                const joiningDateStr = emp.joining_date ? toISTDateString(emp.joining_date) : null;

                // Determine initial status based on conditions
                let initialStatus = 'absent';
                if (joiningDateStr && selectedDateStr < joiningDateStr) {
                    initialStatus = 'inactive';
                } else if (isHoliday) {
                    initialStatus = 'holiday';
                } else if (isWeekOff(selectedDay, emp)) {
                    initialStatus = 'week_off';
                }

                employeeStatusMap.set(empId, {
                    employee_id: empId,
                    id: empId,
                    employee_code: emp.employee_code,
                    first_name: emp.first_name,
                    last_name: emp.last_name,
                    department_name: emp.department_name,
                    status: initialStatus,
                    joining_date: emp.joining_date,
                    weekly_off: emp.weekly_off,
                    shift_start_time: emp.shift_start_time,
                    shift_grace_period: emp.shift_grace_period,
                    check_in_time: null,
                    check_out_time: null,
                    work_hours: 0,
                    work_location: null
                });
            });

            console.log(`📊 Initialized ${employeeStatusMap.size} employees with base statuses`);

            // Step 2: Process attendance data (present employees override holiday/week-off)
            console.log('📊 Step 2: Processing attendance data');
            attendanceData.forEach(record => {
                const empId = getEmployeeId(record);
                const existing = employeeStatusMap.get(empId);

                if (existing) {
                    // Present status can override holiday/week-off/absent but not inactive or leave
                    const currentPriority = STATUS_PRIORITY[existing.status] || 0;
                    const newPriority = STATUS_PRIORITY['present'] || 0;

                    if (existing.status !== 'inactive' && existing.status !== 'leave') {
                        employeeStatusMap.set(empId, {
                            ...existing,
                            ...record,
                            status: 'present',
                            employee_code: record.employee_code || existing.employee_code,
                            first_name: record.first_name || existing.first_name,
                            last_name: record.last_name || existing.last_name,
                            department_name: record.department_name || existing.department_name
                        });
                    }
                } else {
                    console.warn(`⚠️ Attendance record for unknown employee ID: ${empId}`);
                }
            });

            // Step 3: Process leave data (leave overrides most statuses except inactive)
            console.log('📊 Step 3: Processing approved leaves');
            const approvedLeaves = allLeaveData.filter(leave => {
                if (leave.status !== 'approved') return false;

                const leaveStart = new Date(leave.from_date || leave.start_date);
                const leaveEnd = new Date(leave.to_date || leave.end_date);
                leaveStart.setHours(0, 0, 0, 0);
                leaveEnd.setHours(23, 59, 59, 999);

                return selectedDay >= leaveStart && selectedDay <= leaveEnd;
            });

            console.log(`📋 Found ${approvedLeaves.length} employees on approved leave for ${selectedDate}`);

            approvedLeaves.forEach(leave => {
                const empId = getEmployeeId(leave);
                const existing = employeeStatusMap.get(empId);

                if (existing) {
                    // Leave overrides everything except inactive status
                    if (existing.status !== 'inactive') {
                        const nameParts = (leave.employee_name || '').split(' ');
                        const firstName = leave.first_name || nameParts[0] || existing.first_name;
                        const lastName = leave.last_name || nameParts.slice(1).join(' ') || existing.last_name;

                        employeeStatusMap.set(empId, {
                            ...existing,
                            status: 'leave',
                            leave_type: leave.leave_type_name || leave.leave_type,
                            leave_reason: leave.reason,
                            first_name: firstName,
                            last_name: lastName,
                            employee_code: leave.employee_code || existing.employee_code,
                            department_name: leave.department_name || existing.department_name,
                            check_in_time: null,
                            check_out_time: null,
                            work_hours: 0
                        });
                    }
                } else {
                    console.warn(`⚠️ Leave record for unknown employee ID: ${empId}`);
                }
            });

            // Convert map to array - each employee appears exactly once
            const completeAttendanceData = Array.from(employeeStatusMap.values());

            console.log('✅ Deduplicated data:', {
                totalEmployees: completeAttendanceData.length,
                byStatus: {
                    present: completeAttendanceData.filter(e => e.status === 'present').length,
                    absent: completeAttendanceData.filter(e => e.status === 'absent').length,
                    leave: completeAttendanceData.filter(e => e.status === 'leave').length,
                    holiday: completeAttendanceData.filter(e => e.status === 'holiday').length,
                    weekOff: completeAttendanceData.filter(e => e.status === 'week_off').length,
                    inactive: completeAttendanceData.filter(e => e.status === 'inactive').length
                }
            });

            // Calculate clean stats from deduplicated data
            const stats = {
                total: 0,
                present: 0,
                absent: 0,
                leave: 0,
                holiday: 0,
                weekOff: 0,
                inactive: 0,
                wfh: 0,
                office: 0
            };

            completeAttendanceData.forEach(emp => {
                if (emp.status === 'inactive') {
                    stats.inactive++;
                } else {
                    stats.total++; // Only count active employees in total

                    switch(emp.status) {
                        case 'present':
                            stats.present++;
                            // Check WFH/Office location
                            const locationFromWfh = (emp.work_location_from_wfh || '').toLowerCase();
                            const locationFromPunch = (emp.work_location || '').toLowerCase();
                            const locationFromRegularization = (emp.location?.type || '').toLowerCase();

                            const isWFH = locationFromWfh.includes('wfh') ||
                                         locationFromPunch.includes('wfh') ||
                                         locationFromPunch.includes('remote') ||
                                         locationFromPunch.includes('home') ||
                                         locationFromRegularization.includes('wfh');

                            if (isWFH) {
                                stats.wfh++;
                            } else {
                                stats.office++;
                            }
                            break;
                        case 'absent':
                            stats.absent++;
                            break;
                        case 'leave':
                            stats.leave++;
                            break;
                        case 'holiday':
                            stats.holiday++;
                            break;
                        case 'week_off':
                            stats.weekOff++;
                            break;
                    }
                }
            });

            // Validation check
            const sumCheck = stats.present + stats.absent + stats.leave + stats.holiday + stats.weekOff;
            if (sumCheck !== stats.total) {
                console.error('⚠️ Stats validation failed!', {
                    sum: sumCheck,
                    total: stats.total,
                    breakdown: stats
                });
            } else {
                console.log('✅ Stats validation passed:', stats);
            }

            // Verify no duplicates
            const uniqueEmployeeIds = new Set(completeAttendanceData.map(a => getEmployeeId(a)));
            if (uniqueEmployeeIds.size !== completeAttendanceData.length) {
                console.error('⚠️ Duplicate employees detected!', {
                    uniqueCount: uniqueEmployeeIds.size,
                    totalRecords: completeAttendanceData.length
                });
            } else {
                console.log('✅ No duplicates - each employee appears exactly once');
            }

            // Set the state with clean data
            setAttendanceData(completeAttendanceData);
            setStats(stats);
        } catch (error) {
            console.error('Error fetching attendance:', error);
            setAttendanceData([]);
            setStats({
                total: 0,
                present: 0,
                absent: 0,
                leave: 0,
                wfh: 0,
                office: 0,
                holiday: 0,
                weekOff: 0,
                inactive: 0
            });
        } finally {
            setLoading(false);
        }
    };

    // Filtered attendance data based on status filter
    // Get unique departments for filter dropdown
    const uniqueDepartments = useMemo(() => {
        const departments = new Set();
        attendanceData.forEach(record => {
            if (record.department_name) {
                departments.add(record.department_name);
            }
        });
        return Array.from(departments).sort();
    }, [attendanceData]);

    const filteredAttendanceData = useMemo(() => {
        
        return attendanceData.filter(record => {
            // Filter by status
            if (filterStatus !== 'all' && record.status !== filterStatus) {
                return false;
            }

           

            // Filter by employee name
            if (filterEmployee) {
                const fullName = `${record.first_name} ${record.last_name}`.toLowerCase();
                const employeeCode = (record.employee_code || '').toLowerCase();
                const searchTerm = filterEmployee.toLowerCase();
                if (!fullName.includes(searchTerm) && !employeeCode.includes(searchTerm)) {
                    return false;
                }
            }

            // Filter by department
            if (filterDepartment !== 'all' && record.department_name !== filterDepartment) {
                return false;
            }

            // Filter by location (WFH/Office)
            if (filterLocation !== 'all' && record.status === 'present') {
                const locationFromWfh = (record.work_location_from_wfh || '').toLowerCase();
                const locationFromPunch = (record.work_location || '').toLowerCase();
                const locationFromRegularization = (record.location?.type || '').toLowerCase();

                const isWfhFromRequest = locationFromWfh.includes('wfh');
                const isWfhFromPunch = locationFromPunch.includes('wfh') ||
                                     locationFromPunch.includes('remote') ||
                                     locationFromPunch.includes('home');
                const isWfhFromRegularization = locationFromRegularization.includes('wfh');

                const isWFH = isWfhFromRequest || isWfhFromPunch || isWfhFromRegularization;

                if (filterLocation === 'wfh' && !isWFH) {
                    return false;
                }
                if (filterLocation === 'office' && isWFH) {
                    return false;
                }
            }
           
            return true;
        });
    }, [attendanceData, filterStatus, filterEmployee, filterDepartment, filterLocation]);

    const toggleEmployee = (employeeId) => {
        const newExpanded = new Set(expandedEmployees);
        if (newExpanded.has(employeeId)) {
            newExpanded.delete(employeeId);
        } else {
            newExpanded.add(employeeId);
        }
        setExpandedEmployees(newExpanded);
    };

    const getStatusBadge = (status) => {
        const config = {
            present: {
                bg: 'bg-green-100 dark:bg-green-900/30',
                text: 'text-green-700 dark:text-green-400',
                icon: CheckCircle,
                label: 'Present'
            },
            absent: {
                bg: 'bg-red-100 dark:bg-red-900/30',
                text: 'text-red-700 dark:text-red-400',
                icon: XCircle,
                label: 'Absent'
            },
            'half-day': {
                bg: 'bg-yellow-100 dark:bg-yellow-900/30',
                text: 'text-yellow-700 dark:text-yellow-400',
                icon: AlertCircle,
                label: 'Half Day'
            },
            leave: {
                bg: 'bg-blue-100 dark:bg-blue-900/30',
                text: 'text-blue-700 dark:text-blue-400',
                icon: Clock,
                label: 'On Leave'
            },
            holiday: {
                bg: 'bg-orange-100 dark:bg-orange-900/30',
                text: 'text-orange-700 dark:text-orange-400',
                icon: PartyPopper,
                label: 'Holiday'
            },
            week_off: {
                bg: 'bg-purple-100 dark:bg-purple-900/30',
                text: 'text-purple-700 dark:text-purple-400',
                icon: Home,
                label: 'Week Off'
            },
            inactive: {
                bg: 'bg-gray-100 dark:bg-gray-700',
                text: 'text-gray-600 dark:text-gray-400',
                icon: Users,
                label: 'Inactive (Not Joined)'
            }
        };

        const cfg = config[status] || config.absent;
        const Icon = cfg.icon;

        return (
            <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${cfg.bg} ${cfg.text}`}>
                <Icon className="w-3 h-3 mr-1" />
                {cfg.label}
            </span>
        );
    };

    const exportAttendance = () => {
        if (attendanceData.length === 0) {
            alert('No attendance data to export');
            return;
        }

        const csvData = attendanceData.map(record => ({
            'Employee Code': record.employee_code || '-',
            'Name': `${record.first_name} ${record.last_name}`,
            'Department': record.department_name || '-',
            'Check In': record.check_in_time ? formatTime(record.check_in_time) : '-',
            'Check Out': record.check_out_time ? formatTime(record.check_out_time) : '-',
            'Hours': record.work_hours ? `${record.work_hours}h` : '0h',
            'Status': record.status || 'absent'
        }));

        const csv = [
            Object.keys(csvData[0]).join(','),
            ...csvData.map(row => Object.values(row).join(','))
        ].join('\n');

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `attendance_${selectedDate}.csv`;
        a.click();
    };

    // Handler to open employee profile in new tab with Time tab and Attendance active
    const handleEmployeeClick = (employeeId) => {
        const activeTour = currentTour ? getTourById(currentTour, userRole) : null;
        const activeStep = activeTour?.steps?.[tourStep];
        const isMandatoryAttendanceRowStep =
            activeStep?.target === '.tour-attendance-employee-row' &&
            activeStep?.requireClick;

        if (isMandatoryAttendanceRowStep) {
            // During mandatory FTUE click step, keep user on attendance screen
            // so the tour can continue without losing context.
            return;
        }

        // Open in new tab with state parameters
        const url = `/admin/employees/${employeeId}?tab=time&subtab=attendance`;
        window.open(url, '_blank');
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-gray-600 dark:text-gray-400">Loading attendance data...</div>
            </div>
        );
    }

    const handleNavigateToAnalytics = () => {
        // Navigate to analytics dashboard with attendance tab active
        navigate('/admin/analytics-dashboard?tab=attendance');
    };

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900 p-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="tour-attendance-header text-xl font-bold text-gray-900 dark:text-white">Attendance Management</h1>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            Track employee attendance, manage holidays, and monitor work hours
                        </p>
                    </div>
                    {/* Analytics button hidden — accessible via Analytics tab */}
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900">
                <div className="border-b dark:border-gray-700">
                    <nav className="flex space-x-8 px-6">
                        <button
                            onClick={() => setActiveTab('overview')}
                            className={`tour-attendance-tab-overview py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                                activeTab === 'overview'
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                        >
                            Attendance Overview
                        </button>
                        <button
                            onClick={() => setActiveTab('analytics')}
                            className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                                activeTab === 'analytics'
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                        >
                            <BarChart3 className="w-4 h-4" />
                            Analytics
                        </button>
                        <button
                            onClick={() => setActiveTab('holidays')}
                            className={`tour-attendance-tab-holidays py-4 px-2 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                                activeTab === 'holidays'
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                        >
                            <Settings className="w-4 h-4" />
                            Holiday Management
                        </button>
                        <button
                            onClick={() => setActiveTab('leavePlans')}
                            className={`tour-attendance-tab-leave-plans py-4 px-2 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                                activeTab === 'leavePlans'
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                        >
                            <FileText className="w-4 h-4" />
                            Leave Plans
                        </button>
                        <button
                            onClick={() => setActiveTab('regulate')}
                            className={`tour-attendance-tab-regulate py-4 px-2 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                                activeTab === 'regulate'
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                        >
                            <Users className="w-4 h-4" />
                            Regulate Attendance
                        </button>
                        <button
                            onClick={() => setActiveTab('geo')}
                            className={`tour-attendance-tab-geo py-4 px-2 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                                activeTab === 'geo'
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                        >
                            <MapPin className="w-4 h-4" />
                            Geo Location
                        </button>
                        <button
                            onClick={() => setActiveTab('biometrics')}
                            className={`tour-attendance-tab-biometrics py-4 px-2 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                                activeTab === 'biometrics'
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                        >
                            <Settings className="w-4 h-4" />
                            Biometrics
                        </button>
                    </nav>
                </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' ? (
                <div className="space-y-4">
                    {/* Calendar Widget */}
                    {isCalendarExpanded && (
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900">
                            <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-bounce" />
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Select Date</h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Click the dismiss button when done →</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIsCalendarExpanded(false)}
                                    className="relative p-2.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200 group animate-pulse hover:animate-none"
                                    title="Minimize Calendar"
                                >
                                    {/* Pulsating ring effect */}
                                    <span className="absolute inset-0 rounded-lg bg-red-400 dark:bg-red-500 animate-ping opacity-30 group-hover:opacity-0"></span>
                                    <ChevronUp className="relative w-5 h-5 text-red-600 dark:text-red-400 group-hover:scale-110 transition-transform" />
                                </button>
                            </div>
                            <div className="p-4">
                                <AttendanceCalendarEnhanced
                                    selectedDate={selectedDate}
                                    onDateSelect={(date) => {
                                        setSelectedDate(date);
                                        // Scroll to details section smoothly instead of collapsing
                                        setTimeout(() => {
                                            detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                        }, 100);
                                    }}
                                    totalEmployees={totalEmployees}
                                    allEmployees={allEmployees}
                                    onMonthDataLoaded={handleMonthDataLoaded}
                                    cachedData={calendarCache}
                                />
                            </div>
                        </div>
                    )}

                    {/* Attendance Details */}
                    <div ref={detailsRef} className="space-y-4">
                        {/* Date Info & Stats */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900 p-4">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => setIsCalendarExpanded(!isCalendarExpanded)}
                                        className={`tour-attendance-calendar-toggle relative p-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all group ${
                                            !isCalendarExpanded ? 'animate-pulse' : ''
                                        }`}
                                        title={isCalendarExpanded ? "Hide Calendar" : "Show Calendar"}
                                    >
                                        {/* Pulsating ring effect when closed */}
                                        {!isCalendarExpanded && (
                                            <span className="absolute inset-0 rounded-lg bg-blue-400 dark:bg-blue-500 animate-ping opacity-20"></span>
                                        )}
                                        {isCalendarExpanded ? (
                                            <ChevronUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                        ) : (
                                            <Calendar className="relative w-5 h-5 text-blue-600 dark:text-blue-400 group-hover:scale-125 transition-transform" />
                                        )}
                                    </button>
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                            {formatDate(selectedDate, { format: 'full' })}
                                        </h2>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            Click calendar icon to change date
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={exportAttendance}
                                    className="tour-attendance-export px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
                                >
                                    <Download className="w-4 h-4" />
                                    Export
                                </button>
                            </div>

                            {/* Quick Stats */}
                            <div className="tour-attendance-stats grid grid-cols-4 gap-4">
                                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded">
                                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</div>
                                    <div className="text-xs text-gray-600 dark:text-gray-400">Total</div>
                                </div>
                                <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded">
                                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                                        {stats.present}
                                    </div>
                                    <div className="text-xs text-gray-600 dark:text-gray-400">Present</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                        Office: {stats.office} | WFH: {stats.wfh}
                                    </div>
                                </div>
                                <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded">
                                    <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                                        {stats.absent}
                                    </div>
                                    <div className="text-xs text-gray-600 dark:text-gray-400">Absent</div>
                                </div>
                                <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded">
                                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                        {stats.leave}
                                    </div>
                                    <div className="text-xs text-gray-600 dark:text-gray-400">On Leave</div>
                                </div>
                            </div>
                        </div>

                        {/* Employee Attendance Table */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900">
                            <div className="p-4 border-b dark:border-gray-700">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="tour-attendance-details-header text-base font-semibold text-gray-900 dark:text-white">
                                        Employee Attendance Details
                                    </h3>
                                    <div className="flex items-center gap-2">
                                        <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Filter:</span>
                                    </div>
                                </div>

                                {/* Day Navigation */}
                                <div className="tour-attendance-day-navigation flex items-center justify-center gap-4 mb-4 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                    <button
                                        onClick={() => {
                                            const currentDate = parseISTDate(selectedDate) || new Date();
                                            currentDate.setDate(currentDate.getDate() - 1);
                                            setSelectedDate(toISTDateString(currentDate));
                                        }}
                                        className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors"
                                        title="Previous Day"
                                    >
                                        <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                    </button>
                                    <div className="text-center min-w-[200px]">
                                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                                            {formatDate(selectedDate, { format: 'full' })}
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => {
                                            const currentDate = parseISTDate(selectedDate) || new Date();
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            currentDate.setDate(currentDate.getDate() + 1);
                                            // Don't allow navigating to future dates
                                            if (currentDate <= today) {
                                                setSelectedDate(toISTDateString(currentDate));
                                            }
                                        }}
                                        disabled={(() => {
                                            const currentDate = parseISTDate(selectedDate) || new Date();
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            currentDate.setHours(0, 0, 0, 0);
                                            return currentDate >= today;
                                        })()}
                                        className={`p-2 rounded-full transition-colors ${
                                            (() => {
                                                const currentDate = parseISTDate(selectedDate) || new Date();
                                                const today = new Date();
                                                today.setHours(0, 0, 0, 0);
                                                currentDate.setHours(0, 0, 0, 0);
                                                return currentDate >= today;
                                            })()
                                                ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                                : 'hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-400'
                                        }`}
                                        title="Next Day"
                                    >
                                        <ChevronRight className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => setSelectedDate(getLocalDateString())}
                                        className="tour-attendance-day-today ml-2 px-3 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                                    >
                                        Today
                                    </button>
                                </div>
                                {/* Filter Buttons */}
                                <div className="tour-attendance-status-filters flex flex-wrap gap-2">
                                    <button
                                        onClick={() => setFilterStatus('all')}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                            filterStatus === 'all'
                                                ? 'bg-gray-600 text-white dark:bg-gray-500'
                                                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                        }`}
                                    >
                                        All ({stats.total})
                                    </button>
                                    <button
                                        onClick={() => setFilterStatus('present')}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                                            filterStatus === 'present'
                                                ? 'bg-green-600 text-white dark:bg-green-500'
                                                : 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/30'
                                        }`}
                                    >
                                        <CheckCircle className="w-3.5 h-3.5" />
                                        Present ({stats.present})
                                    </button>
                                    <button
                                        onClick={() => setFilterStatus('absent')}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                                            filterStatus === 'absent'
                                                ? 'bg-red-600 text-white dark:bg-red-500'
                                                : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/30'
                                        }`}
                                    >
                                        <XCircle className="w-3.5 h-3.5" />
                                        Absent ({stats.absent})
                                    </button>
                                    <button
                                        onClick={() => setFilterStatus('leave')}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                                            filterStatus === 'leave'
                                                ? 'bg-blue-600 text-white dark:bg-blue-500'
                                                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/30'
                                        }`}
                                    >
                                        <Clock className="w-3.5 h-3.5" />
                                        On Leave ({stats.leave})
                                    </button>
                                    <button
                                        onClick={() => setFilterStatus('inactive')}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                                            filterStatus === 'inactive'
                                                ? 'bg-gray-600 text-white dark:bg-gray-500'
                                                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                        }`}
                                    >
                                        <Users className="w-3.5 h-3.5" />
                                        Inactive ({stats.inactive || 0})
                                    </button>
                                    <button
                                        onClick={() => setFilterStatus('holiday')}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                                            filterStatus === 'holiday'
                                                ? 'bg-orange-600 text-white dark:bg-orange-500'
                                                : 'bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/30'
                                        }`}
                                    >
                                        <PartyPopper className="w-3.5 h-3.5" />
                                        Holiday ({stats.holiday || 0})
                                    </button>
                                    <button
                                        onClick={() => setFilterStatus('week_off')}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                                            filterStatus === 'week_off'
                                                ? 'bg-purple-600 text-white dark:bg-purple-500'
                                                : 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400 hover:bg-purple-200 dark:hover:bg-purple-900/30'
                                        }`}
                                    >
                                        <Home className="w-3.5 h-3.5" />
                                        Week Off ({stats.weekOff || 0})
                                    </button>
                                </div>

                                {/* Clear Filters Button */}
                                {(filterEmployee || filterDepartment !== 'all' || filterLocation !== 'all' || filterStatus !== 'all') && (
                                    <button
                                        onClick={() => {
                                            setFilterEmployee('');
                                            setFilterDepartment('all');
                                            setFilterLocation('all');
                                            setFilterStatus('all');
                                        }}
                                        className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500 transition-all flex items-center gap-1"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                        Clear All Filters
                                    </button>
                                )}
                            </div>
                            <div className="overflow-x-auto">
                                <div className="max-h-[600px] overflow-y-auto">
                                    <table className="tour-attendance-table w-full">
                                        <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                                            <tr>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                                                    Employee
                                                </th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                                                    <div className="flex items-center gap-2">
                                                        <span>Department</span>
                                                    </div>
                                                </th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                                                    <div className="flex items-center gap-2">
                                                        <span>Location</span>
                                                        <Filter className="w-3 h-3 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200" />
                                                    </div>
                                                </th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Time Check</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Check In</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Check Out</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Hours</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                                                    <div className="flex items-center gap-2">
                                                        <span>Status</span>
                                                        <Filter className="w-3 h-3 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200" />
                                                    </div>
                                                </th>
                                            </tr>
                                            {/* Filter Row */}
                                            <tr className="bg-gray-100 dark:bg-gray-600">
                                                <th className="px-4 py-2">
                                                    <input
                                                        type="text"
                                                        placeholder="Search employee..."
                                                        value={filterEmployee}
                                                        onChange={(e) => setFilterEmployee(e.target.value)}
                                                        className="tour-attendance-search w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </th>
                                                <th className="px-4 py-2">
                                                    <select
                                                        value={filterDepartment}
                                                        onChange={(e) => setFilterDepartment(e.target.value)}
                                                        className="tour-attendance-department-filter w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                    >
                                                        <option value="all">All Departments</option>
                                                        {uniqueDepartments.map(dept => (
                                                            <option key={dept} value={dept}>{dept}</option>
                                                        ))}
                                                    </select>
                                                </th>
                                                <th className="px-4 py-2">
                                                    <select
                                                        value={filterLocation}
                                                        onChange={(e) => setFilterLocation(e.target.value)}
                                                        className="tour-attendance-location-filter w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                    >
                                                        <option value="all">All</option>
                                                        <option value="wfh">WFH</option>
                                                        <option value="office">Office</option>
                                                    </select>
                                                </th>
                                                <th className="px-4 py-2"></th>
                                                <th className="px-4 py-2"></th>
                                                <th className="px-4 py-2"></th>
                                                <th className="px-4 py-2"></th>
                                                <th className="px-4 py-2">
                                                    <select
                                                        value={filterStatus}
                                                        onChange={(e) => setFilterStatus(e.target.value)}
                                                        className="tour-attendance-status-filter w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                    >
                                                        <option value="all">All Status</option>
                                                        <option value="present">Present</option>
                                                        <option value="absent">Absent</option>
                                                        <option value="leave">Leave</option>
                                                        <option value="holiday">Holiday</option>
                                                        <option value="week_off">Week Off</option>
                                                        <option value="inactive">Inactive</option>
                                                    </select>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y dark:divide-gray-700">
                                        {filteredAttendanceData.length === 0 ? (
                                            <tr>
                                                <td colSpan="8" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                                                    {filterStatus === 'all'
                                                        ? 'No attendance records found for this date'
                                                        : `No ${filterStatus} employees found`
                                                    }
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredAttendanceData.map(record => (
                                                <tr
                                                    key={record.employee_id}
                                                    className="tour-attendance-employee-row hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                                                    onClick={() => handleEmployeeClick(record.employee_id)}
                                                    title="Click to view employee profile"
                                                >
                                                    <td className="px-4 py-3">
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                                {record.first_name} {record.last_name}
                                                            </p>
                                                            <p className="text-xs text-gray-500 dark:text-gray-400">{record.employee_code}</p>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                                        {record.department_name || '-'}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        {record.status === 'present' ? (
                                                            (() => {
                                                                // Check both work_location_from_wfh (WFH request) and work_location (punch record)
                                                                const locationFromWfh = (record.work_location_from_wfh || '').toLowerCase();
                                                                const locationFromPunch = (record.work_location || '').toLowerCase();
                                                                const locationFromRegularization = (record.location?.type || '').toLowerCase();

                                                                const isWfhFromRequest = locationFromWfh.includes('wfh');
                                                                const isWfhFromPunch = locationFromPunch === 'wfh' ||
                                                                                     locationFromPunch.includes('home');
                                                                const isLocationFromRegularization = locationFromRegularization.includes('wfh');
                                                                const isFlagged = locationFromPunch === 'flagged';
                                                                const isRemote = locationFromPunch === 'remote';

                                                                const isWFH = isWfhFromRequest || isWfhFromPunch || isLocationFromRegularization;

                                                                if (isFlagged) {
                                                                    return (
                                                                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">
                                                                            <AlertCircle className="w-3 h-3 mr-1" />
                                                                            Unverified
                                                                        </span>
                                                                    );
                                                                }
                                                                if (isRemote) {
                                                                    return (
                                                                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
                                                                            <MapPin className="w-3 h-3 mr-1" />
                                                                            Remote
                                                                        </span>
                                                                    );
                                                                }
                                                                return isWFH ? (
                                                                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400">
                                                                        <Home className="w-3 h-3 mr-1" />
                                                                        WFH
                                                                    </span>
                                                                ) : (
                                                                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                                                        <Users className="w-3 h-3 mr-1" />
                                                                        Office
                                                                    </span>
                                                                );
                                                            })()
                                                        ) : (
                                                            <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        {record.status === 'present' && record.check_in_time ? (
                                                            (() => {
                                                                const timeStatus = getTimeCheckStatus(
                                                                    record.check_in_time,
                                                                    record.shift_start_time,
                                                                    record.shift_grace_period
                                                                );
                                                                if (timeStatus === 'early') {
                                                                    return (
                                                                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                                                                            <Clock className="w-3 h-3 mr-1" />
                                                                            Early
                                                                        </span>
                                                                    );
                                                                } else if (timeStatus === 'on-time') {
                                                                    return (
                                                                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                                                                            <CheckCircle className="w-3 h-3 mr-1" />
                                                                            On Time
                                                                        </span>
                                                                    );
                                                                } else if (timeStatus === 'late') {
                                                                    return (
                                                                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
                                                                            <AlertCircle className="w-3 h-3 mr-1" />
                                                                            Late
                                                                        </span>
                                                                    );
                                                                }
                                                                return <span className="text-sm text-gray-400 dark:text-gray-500">-</span>;
                                                            })()
                                                        ) : (
                                                            <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                                        {record.check_in_time ? formatTime(record.check_in_time) : '-'}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                                        {record.check_out_time ? formatTime(record.check_out_time) : '-'}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                                        {record.work_hours ? `${record.work_hours}h` : '0h'}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        {getStatusBadge(record.status || 'absent')}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : activeTab === 'analytics' ? (
                <AttendanceAnalytics />
            ) : activeTab === 'holidays' ? (
                <HolidayManagement />
            ) : activeTab === 'leavePlans' ? (
                <LeavePlanManagement />
            ) : activeTab === 'geo' ? (
                <GeoLocation />
            ) : activeTab === 'biometrics' ? (
                <BiometricManagement />
            ) : (
                <RegulateAttendance />
            )}
        </div>
    );
};

export default ComprehensiveAttendanceManagement;
